// Toast utility functions using react-hot-toast
import { toast as hotToast, ToastOptions } from 'react-hot-toast';

interface CustomToastOptions extends ToastOptions {
  theme?: 'light' | 'dark';
}

const getToastStyles = (theme: 'light' | 'dark' = 'light') => ({
  background: theme === 'dark' ? 'var(--color-bg-elevated)' : 'var(--color-bg-primary)',
  color: theme === 'dark' ? 'var(--color-text-primary)' : 'var(--color-text-primary)',
  border: `1px solid ${theme === 'dark' ? 'var(--color-border-primary)' : 'var(--color-border-primary)'}`,
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-lg)',
});

export const toast = {
  success: (message: string, options?: CustomToastOptions) => {
    return hotToast.success(message, {
      style: {
        ...getToastStyles(options?.theme),
        borderColor: 'var(--color-status-success)',
      },
      iconTheme: {
        primary: 'var(--color-status-success)',
        secondary: 'var(--color-bg-primary)',
      },
      ...options,
    });
  },

  error: (message: string, options?: CustomToastOptions) => {
    return hotToast.error(message, {
      style: {
        ...getToastStyles(options?.theme),
        borderColor: 'var(--color-status-error)',
      },
      iconTheme: {
        primary: 'var(--color-status-error)',
        secondary: 'var(--color-bg-primary)',
      },
      ...options,
    });
  },

  info: (message: string, options?: CustomToastOptions) => {
    return hotToast(message, {
      style: {
        ...getToastStyles(options?.theme),
        borderColor: 'var(--color-status-info)',
      },
      icon: 'ℹ️',
      ...options,
    });
  },

  warning: (message: string, options?: CustomToastOptions) => {
    return hotToast(message, {
      style: {
        ...getToastStyles(options?.theme),
        borderColor: 'var(--color-status-warning)',
      },
      icon: '⚠️',
      ...options,
    });
  },

  loading: (message: string, options?: CustomToastOptions) => {
    return hotToast.loading(message, {
      style: getToastStyles(options?.theme),
      ...options,
    });
  },

  custom: (message: string, options?: CustomToastOptions) => {
    return hotToast(message, {
      style: getToastStyles(options?.theme),
      ...options,
    });
  },

  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
    },
    options?: CustomToastOptions
  ) => {
    return hotToast.promise(promise, messages, {
      style: getToastStyles(options?.theme),
      success: {
        style: {
          ...getToastStyles(options?.theme),
          borderColor: 'var(--color-status-success)',
        },
        iconTheme: {
          primary: 'var(--color-status-success)',
          secondary: 'var(--color-bg-primary)',
        },
      },
      error: {
        style: {
          ...getToastStyles(options?.theme),
          borderColor: 'var(--color-status-error)',
        },
        iconTheme: {
          primary: 'var(--color-status-error)',
          secondary: 'var(--color-bg-primary)',
        },
      },
      loading: {
        style: getToastStyles(options?.theme),
      },
      ...options,
    });
  },

  dismiss: (toastId?: string) => {
    return hotToast.dismiss(toastId);
  },

  remove: (toastId?: string) => {
    return hotToast.remove(toastId);
  },
};